/**
 * The console's direct line to the CRM server (ADR 0021, ADR 0023).
 *
 * React CRM pages call the CRM with the console's Supabase JWT (the CRM
 * verifies the same token) and reach OnlyFans through the CRM's gateway
 * pass-through, so the CRM keeps applying its permission checks.
 */

import { LOGGED_OUT_MESSAGE, isLoggedOutEnvelope, noteGatewayEnvelope } from '../accountSession'

export interface CrmClientOptions {
  /** The console's current access token. */
  getToken: () => string | null
  /** Refreshes the console session (it owns the refresh token, ADR 0020). */
  refreshToken: () => Promise<string | null>
  fetchImpl?: typeof fetch
}

export interface CrmClient {
  /** POST an OnlyFans operation through the CRM's gateway pass-through. */
  gateway: <T = unknown>(accountId: string, path: string, body?: Record<string, unknown>) => Promise<T>
  /** POST to a CRM-local route (permissions, media stats, CDN); the raw response. */
  post: (accountId: string, path: string, body: Record<string, unknown>) => Promise<Response>
  /** GET a CRM-local route. */
  get: (accountId: string, path: string) => Promise<Response>
}

const CRM_BASE = '/crm'

/**
 * How long an OnlyFans read may take before the page gives up and offers a
 * retry. The gateway retries a dead proxy for up to two minutes and the CRM
 * proxy waits 200 s, so without this a list spins for minutes.
 */
export const CRM_READ_TIMEOUT_MS = 30_000

/**
 * Gateway reads that may be timed out. Writes are never cut off: OnlyFans may
 * still act on them, and a retry would send twice.
 */
const READ_PATHS = new Set([
  '/v1/chats/get',
  '/v1/users/list',
  '/v1/users/profile',
  '/v1/lists/get',
  '/v1/lists/get-users',
  '/v1/vault/media',
  '/v1/vault/lists',
])

function isRead(path: string, body: Record<string, unknown>): boolean {
  if (path === '/v1/of/raw') return String(body.method ?? '').toUpperCase() === 'GET'
  return READ_PATHS.has(path)
}

/** A CRM or OnlyFans failure; `status` is OnlyFans' own when the gateway relays one. */
export class CrmError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** OnlyFans' own error body when the gateway relays one (e.g. `{ error: { code, message } }`). */
    readonly data?: unknown,
  ) {
    super(message)
    this.name = 'CrmError'
  }
}

type Envelope = {
  ok?: boolean
  status_code?: number
  data?: unknown
  detail?: unknown
  error?: unknown
}

function reason(body: Envelope | null, fallback: string): string {
  const nested = (body?.data as { error?: { message?: unknown } } | undefined)?.error?.message
  for (const r of [body?.detail, body?.error, nested]) {
    if (typeof r === 'string' && r) return r
  }
  return fallback
}

export function createCrmClient({ getToken, refreshToken, fetchImpl = fetch }: CrmClientOptions): CrmClient {
  /**
   * One request; a 401 refreshes the console session once and retries. With
   * `timeoutMs` the whole exchange, retry included, fails with a 504 CrmError
   * once that time is up.
   */
  const send = async (url: string, init: RequestInit, timeoutMs?: number) => {
    const controller = timeoutMs ? new AbortController() : undefined
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : undefined
    const attempt = (token: string | null) =>
      fetchImpl(url, {
        ...init,
        headers: { ...init.headers, Authorization: `Bearer ${token ?? ''}` },
        ...(controller ? { signal: controller.signal } : {}),
      })
    try {
      const res = await attempt(getToken())
      if (res.status !== 401) return res
      const fresh = await refreshToken()
      return fresh ? await attempt(fresh) : res
    } catch (e) {
      if (controller?.signal.aborted) throw new CrmError('OnlyFans took too long to answer. Try again.', 504)
      throw e
    } finally {
      clearTimeout(timer)
    }
  }

  /** The same OnlyFans read, asked again while the first is still out, shares it. */
  const reads = new Map<string, Promise<unknown>>()

  const call = async <T,>(accountId: string, path: string, body: Record<string, unknown>): Promise<T> => {
    const timeoutMs = isRead(path, body) ? CRM_READ_TIMEOUT_MS : undefined
    const res = await send(
      `${CRM_BASE}/proxy${path}`,
      {
        method: 'POST',
        headers: { 'X-Account-Id': accountId, 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_id: accountId, ...body }),
      },
      timeoutMs,
    )
    const envelope = (await res.json().catch(() => null)) as Envelope | null
    if (res.ok) noteGatewayEnvelope(accountId, envelope)
    if (!res.ok) throw new CrmError(reason(envelope, res.statusText || `HTTP ${res.status}`), res.status)
    if (envelope?.ok === false) {
      const status = envelope.status_code ?? 502
      if (isLoggedOutEnvelope(envelope)) throw new CrmError(LOGGED_OUT_MESSAGE, 401)
      throw new CrmError(reason(envelope, `OnlyFans answered ${status}`), status, envelope.data)
    }
    return envelope?.data as T
  }

  return {
    gateway<T>(accountId: string, path: string, body: Record<string, unknown> = {}) {
      // A page mounted twice in a row (or two widgets on one page) must not
      // ask OnlyFans twice: raw GETs are rate-limited and some take seconds.
      if (path !== '/v1/of/raw' || String(body.method ?? '').toUpperCase() !== 'GET') return call<T>(accountId, path, body)
      const key = `${accountId} ${JSON.stringify(body)}`
      const out = reads.get(key)
      if (out) return out as Promise<T>
      const read = call<T>(accountId, path, body).finally(() => reads.delete(key))
      reads.set(key, read)
      return read
    },
    post(accountId, path, body) {
      return send(`${CRM_BASE}${path}`, {
        method: 'POST',
        headers: { 'X-Account-Id': accountId, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
    },
    get(accountId, path) {
      return send(`${CRM_BASE}${path}`, { headers: { 'X-Account-Id': accountId }, cache: 'no-store' }, CRM_READ_TIMEOUT_MS)
    },
  }
}
